import { Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Dumbbell, Calendar, History, ArrowRight } from "lucide-react";
import { ThemeToggle } from "../components/theme/theme-toggle";
import { useEffect } from "react";
import { getCurrentUser } from "../services/auth";
import heroImage from "../assets/fitness-hero.svg";
import { motion } from "framer-motion";

export default function HomePage() {
  const navigate = useNavigate();
  useEffect(() => {
    (async () => {
      const user = await getCurrentUser();
      if (user) {
        navigate("/activity/workout"); // or your dashboard/home route for signed-in users
      }
    })();
  }, [navigate]);

  return (
    <div className="min-h-screen flex flex-col">
      <header className="border-b">
        <div className="container flex h-16 items-center justify-between">
          <Link to="/landing" className="font-bold text-xl">
            Workout App
          </Link>
          <div className="flex items-center gap-4">
            <ThemeToggle />
            <Link to="/auth">
              <Button>Sign In</Button>
            </Link>
          </div>
        </div>
      </header>

      <main className="flex-1">
        <section className="relative py-12 md:py-20 bg-gradient-to-b from-background to-muted overflow-hidden">
          <div className="container px-4 md:px-6 flex flex-col md:flex-row items-center gap-10">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              className="flex-1 flex flex-col items-center md:items-start space-y-4 text-center md:text-left z-10"
            >
              <div className="space-y-2">
                <h1 className="text-4xl font-extrabold tracking-tighter sm:text-5xl md:text-6xl lg:text-7xl bg-gradient-to-r from-primary to-pink-500 bg-clip-text text-transparent drop-shadow-lg">
                  Track Your Fitness Journey
                </h1>
                <p className="mx-auto md:mx-0 max-w-[700px] text-muted-foreground md:text-xl">
                  Create custom workouts, track your progress, and achieve your fitness goals with our powerful workout tracking app.
                </p>
              </div>
              <div className="space-x-4 pt-4">
                <Link to="/auth?signup=true">
                  <Button size="lg" className="px-8 shadow-lg hover:scale-105 transition-transform">
                    Sign Up Free
                  </Button>
                </Link>
                <Link to="/auth">
                  <Button variant="outline" size="lg" className="px-8 hover:bg-primary/10 transition-colors">
                    Log In
                  </Button>
                </Link>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="flex-1 flex justify-center md:justify-end z-0"
            >
              <img
                src={heroImage}
                alt="Fitness Hero"
                className="w-full max-w-md drop-shadow-2xl rounded-xl border border-muted"
                loading="lazy"
              />
            </motion.div>
          </div>
        </section>

        <section className="py-12 md:py-20">
          <div className="container px-4 md:px-6">
            <div className="grid gap-6 md:grid-cols-3">
              <motion.div whileHover={{ scale: 1.05 }} className="flex flex-col items-center space-y-4 rounded-lg border p-6 shadow-md bg-white/80 dark:bg-black/30 transition-transform">
                <div className="p-3 rounded-full bg-primary/10">
                  <Dumbbell className="h-6 w-6 text-primary" />
                </div>
                <h3 className="text-xl font-bold">Custom Workouts</h3>
                <p className="text-center text-muted-foreground">
                  Create personalized workout routines tailored to your specific goals and preferences.
                </p>
              </motion.div>
              <motion.div whileHover={{ scale: 1.05 }} className="flex flex-col items-center space-y-4 rounded-lg border p-6 shadow-md bg-white/80 dark:bg-black/30 transition-transform">
                <div className="p-3 rounded-full bg-primary/10">
                  <Calendar className="h-6 w-6 text-primary" />
                </div>
                <h3 className="text-xl font-bold">Track Progress</h3>
                <p className="text-center text-muted-foreground">
                  Monitor your workouts and see your improvement over time with detailed statistics.
                </p>
              </motion.div>
              <motion.div whileHover={{ scale: 1.05 }} className="flex flex-col items-center space-y-4 rounded-lg border p-6 shadow-md bg-white/80 dark:bg-black/30 transition-transform">
                <div className="p-3 rounded-full bg-primary/10">
                  <History className="h-6 w-6 text-primary" />
                </div>
                <h3 className="text-xl font-bold">Workout History</h3>
                <p className="text-center text-muted-foreground">
                  Review your past workouts and analyze your performance to optimize future sessions.
                </p>
              </motion.div>
            </div>
          </div>
        </section>

        <section className="py-12 md:py-20 bg-muted">
          <div className="container px-4 md:px-6">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="flex flex-col items-center justify-center space-y-4 text-center"
            >
              <div className="space-y-2">
                <h2 className="text-3xl font-bold tracking-tighter md:text-4xl">
                  Ready to start your fitness journey?
                </h2>
                <p className="mx-auto max-w-[600px] text-muted-foreground md:text-xl">
                  Join thousands of users who have transformed their fitness routine with our app.
                </p>
              </div>
              <Link to="/auth?signup=true">
                <Button size="lg" className="px-8 shadow-lg">
                  Create Free Account <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <blockquote className="italic text-muted-foreground mt-6 max-w-xl mx-auto">
                "The only bad workout is the one that didn't happen."
              </blockquote>
            </motion.div>
          </div>
        </section>
      </main>

      <footer className="border-t py-6 md:py-0">
        <div className="container flex flex-col items-center justify-between gap-4 md:h-16 md:flex-row">
          <p className="text-center text-sm text-muted-foreground md:text-left">
            &copy; {new Date().getFullYear()} Workout App. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
